import Phaser from 'phaser';

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Карта мира с туманом войны. Игрок видит только клетки, которые посетил.
 * Платформы рисуются только в видимых клетках. UI открывается через toggle()
 * — затемняет экран и показывает карту в готической рамке. На время открытия
 * физика мира ставится на паузу.
 */
export class MapSystem {
  private scene: Phaser.Scene;
  private worldW: number;
  private worldH: number;
  private cellSize = 80;
  private cols: number;
  private rows: number;
  private visited: Uint8Array;

  private platforms: Rect[] = [];

  private container?: Phaser.GameObjects.Container;
  private playerMarkerTween?: Phaser.Tweens.Tween;
  private isOpen = false;

  private lastPlayerX = 0;
  private lastPlayerY = 0;

  constructor(scene: Phaser.Scene, worldW: number, worldH: number) {
    this.scene = scene;
    this.worldW = worldW;
    this.worldH = worldH;
    this.cols = Math.ceil(worldW / this.cellSize);
    this.rows = Math.ceil(worldH / this.cellSize);
    this.visited = new Uint8Array(this.cols * this.rows);
  }

  registerPlatform(cx: number, cy: number, w: number, h: number) {
    this.platforms.push({ x: cx - w / 2, y: cy - h / 2, w, h });
  }

  updatePlayer(x: number, y: number) {
    this.lastPlayerX = x;
    this.lastPlayerY = y;
    const RADIUS = 3; // в клетках
    const px = Math.floor(x / this.cellSize);
    const py = Math.floor(y / this.cellSize);
    for (let dy = -RADIUS; dy <= RADIUS; dy++) {
      for (let dx = -RADIUS; dx <= RADIUS; dx++) {
        if (dx * dx + dy * dy > RADIUS * RADIUS) continue;
        const cx = px + dx;
        const cy = py + dy;
        if (cx < 0 || cx >= this.cols || cy < 0 || cy >= this.rows) continue;
        this.visited[cy * this.cols + cx] = 1;
      }
    }
  }

  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }

  isMapOpen(): boolean {
    return this.isOpen;
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.scene.physics.world.pause();

    const cam = this.scene.cameras.main;
    const cw = cam.width;
    const ch = cam.height;

    this.container = this.scene.add.container(0, 0);
    this.container.setScrollFactor(0);
    this.container.setDepth(2500);

    // Затемняющий оверлей на весь экран
    const dim = this.scene.add.rectangle(0, 0, cw, ch, 0x000000, 0.72);
    dim.setOrigin(0, 0);
    this.container.add(dim);

    // Размеры карты — пропорция мира 10240:2880 ≈ 3.56:1
    const mapW = Math.min(880, cw - 100);
    const mapH = mapW * (this.worldH / this.worldW);
    const mapX = Math.round((cw - mapW) / 2);
    const mapY = Math.round((ch - mapH) / 2);

    this.drawGothicFrame(mapX, mapY, mapW, mapH);

    // Тёмное полотно карты — почти чёрное, чуть синеватое (туман)
    const bg = this.scene.add.rectangle(mapX, mapY, mapW, mapH, 0x080c14, 1);
    bg.setOrigin(0, 0);
    this.container.add(bg);

    const sx = mapW / this.worldW;
    const sy = mapH / this.worldH;

    // Открытые клетки — лёгкая каменная заливка
    const fog = this.scene.add.graphics();
    fog.fillStyle(0x1a2030, 1);
    for (let cy = 0; cy < this.rows; cy++) {
      for (let cx = 0; cx < this.cols; cx++) {
        if (!this.visited[cy * this.cols + cx]) continue;
        const px = mapX + cx * this.cellSize * sx;
        const py = mapY + cy * this.cellSize * sy;
        // +0.6 для микрооверлапа — иначе видны чёрные швы между клетками
        const pw = this.cellSize * sx + 0.6;
        const ph = this.cellSize * sy + 0.6;
        fog.fillRect(px, py, pw, ph);
      }
    }
    this.container.add(fog);

    // Платформы — только в открытых клетках
    const plat = this.scene.add.graphics();
    plat.fillStyle(0x9caebf, 1);
    for (const p of this.platforms) {
      const platCellX = Math.floor((p.x + p.w / 2) / this.cellSize);
      const platCellY = Math.floor((p.y + p.h / 2) / this.cellSize);
      if (platCellX < 0 || platCellX >= this.cols) continue;
      if (platCellY < 0 || platCellY >= this.rows) continue;
      if (!this.visited[platCellY * this.cols + platCellX]) continue;
      const px = mapX + p.x * sx;
      const py = mapY + p.y * sy;
      const pw = Math.max(2, p.w * sx);
      const ph = Math.max(1.5, p.h * sy);
      plat.fillRect(px, py, pw, ph);
    }
    this.container.add(plat);

    // Маркер игрока — мерцающая точка
    const markerX = mapX + this.lastPlayerX * sx;
    const markerY = mapY + this.lastPlayerY * sy;
    const marker = this.scene.add.circle(markerX, markerY, 4, 0xff5577, 1);
    marker.setStrokeStyle(1.5, 0xffffff, 0.9);
    this.playerMarkerTween = this.scene.tweens.add({
      targets: marker,
      alpha: 0.35,
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    });
    this.container.add(marker);

    // Заголовок и подсказка
    const title = this.scene.add
      .text(cw / 2, mapY - 30, 'Карта', {
        font: '22px serif',
        color: '#c8a070',
      })
      .setOrigin(0.5, 1);
    this.container.add(title);

    const hint = this.scene.add
      .text(cw / 2, mapY + mapH + 18, 'M / Esc — закрыть', {
        font: '11px monospace',
        color: '#7c8092',
      })
      .setOrigin(0.5, 0);
    this.container.add(hint);
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.scene.physics.world.resume();
    if (this.playerMarkerTween) {
      this.playerMarkerTween.stop();
      this.playerMarkerTween = undefined;
    }
    if (this.container) {
      this.container.destroy();
      this.container = undefined;
    }
  }

  private drawGothicFrame(mapX: number, mapY: number, mapW: number, mapH: number) {
    if (!this.container) return;
    const PAD = 22;
    const g = this.scene.add.graphics();

    // Внешняя тёмная каменная рамка
    g.fillStyle(0x141a22, 1);
    g.fillRect(mapX - PAD, mapY - PAD, mapW + PAD * 2, mapH + PAD * 2);
    // Лёгкий блик сверху на рамке
    g.fillStyle(0x2a3240, 1);
    g.fillRect(mapX - PAD, mapY - PAD, mapW + PAD * 2, 2);

    // Контурная резкая линия
    g.lineStyle(2, 0x0a0d12, 1);
    g.strokeRect(mapX - PAD, mapY - PAD, mapW + PAD * 2, mapH + PAD * 2);

    // Бронзовый внутренний кант
    g.lineStyle(2, 0x8a6438, 0.9);
    g.strokeRect(mapX - 5, mapY - 5, mapW + 10, mapH + 10);

    // Декоративные арочные углы (треугольники-вырезы по 4 углам)
    const C = 16;
    g.fillStyle(0x8a6438, 0.85);
    // top-left
    g.fillTriangle(
      mapX - PAD, mapY - PAD,
      mapX - PAD + C, mapY - PAD,
      mapX - PAD, mapY - PAD + C,
    );
    // top-right
    g.fillTriangle(
      mapX + mapW + PAD, mapY - PAD,
      mapX + mapW + PAD - C, mapY - PAD,
      mapX + mapW + PAD, mapY - PAD + C,
    );
    // bottom-left
    g.fillTriangle(
      mapX - PAD, mapY + mapH + PAD,
      mapX - PAD + C, mapY + mapH + PAD,
      mapX - PAD, mapY + mapH + PAD - C,
    );
    // bottom-right
    g.fillTriangle(
      mapX + mapW + PAD, mapY + mapH + PAD,
      mapX + mapW + PAD - C, mapY + mapH + PAD,
      mapX + mapW + PAD, mapY + mapH + PAD - C,
    );

    // Центральные «гвозди» по серединам сторон — атмосфера
    g.fillStyle(0x6a4a28, 1);
    g.fillCircle(mapX - PAD / 2, mapY + mapH / 2, 3);
    g.fillCircle(mapX + mapW + PAD / 2, mapY + mapH / 2, 3);
    g.fillCircle(mapX + mapW / 2, mapY - PAD / 2, 3);
    g.fillCircle(mapX + mapW / 2, mapY + mapH + PAD / 2, 3);

    this.container.add(g);
  }
}
